import ReactDOM from 'react-dom/client';
import App from './App';
import {store} from './store';
import {Provider} from 'react-redux';
import i18next from 'i18next';
import {initReactI18next} from 'react-i18next';
import ICU from 'i18next-icu';
import 'react-loading-skeleton/dist/skeleton.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/tailwind.css';
import './styles/index.scss';
import {migrateSgfHistoryStorage} from '@utils/sgfHistoryStorage';

const TRANSLATIONS_EXPORT_COMMAND =
    'php -d memory_limit=-1 bin/console app:translations-export /assets/react/translations --lang=en';
const VITE_PRELOAD_RELOAD_KEY_PREFIX = 'vite-preload-reload:';

const registerVitePreloadErrorHandler = () => {
    if (typeof window === 'undefined' || !import.meta.env.PROD) {
        return;
    }

    window.addEventListener('vite:preloadError', event => {
        const reloadKey = `${VITE_PRELOAD_RELOAD_KEY_PREFIX}${event.payload.message}`;

        try {
            if (window.sessionStorage.getItem(reloadKey)) {
                return;
            }

            window.sessionStorage.setItem(reloadKey, 'true');
        } catch (error) {
            console.warn(
                'Unable to store Vite preload-error retry state:',
                error
            );
            return;
        }

        event.preventDefault();
        window.location.reload();
    });
};

registerVitePreloadErrorHandler();
migrateSgfHistoryStorage();

const getDefaultTranslations = (): Record<string, unknown> | null => {
    const translations = import.meta.glob<{default: Record<string, unknown>}>(
        './translations/en.json',
        {eager: true}
    );

    return translations['./translations/en.json']?.default || null;
};

const renderMissingTranslationsMessage = () => {
    const root = document.getElementById('root');
    if (!root) {
        throw new Error('React root element was not found.');
    }

    root.innerHTML = `
        <div style="max-width: 760px; margin: 48px auto; padding: 24px; font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; line-height: 1.5;">
            <h1 style="font-size: 22px; margin: 0 0 12px;">React translations are missing</h1>
            <p style="margin: 0 0 16px;">Generate the English translation file before starting the React app.</p>
            <pre style="white-space: pre-wrap; background: #f6f8fa; border: 1px solid #d0d7de; border-radius: 6px; padding: 12px; margin: 0;"><code>${TRANSLATIONS_EXPORT_COMMAND}</code></pre>
        </div>
    `;
};

const defaultTranslations = getDefaultTranslations();

if (defaultTranslations) {
    void i18next
        .use(ICU)
        .use(initReactI18next)
        .init({
            lng: 'en',
            fallbackLng: 'en',
            debug: false,
            resources: {
                en: {translation: defaultTranslations},
            },
            interpolation: {
                escapeValue: false,
            },
            react: {
                useSuspense: false,
            },
        });
}
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    window.addEventListener('load', async () => {
        try {
            const registration =
                await navigator.serviceWorker.register('/sw.js');
            console.log(
                'Service Worker registered with scope:',
                registration.scope
            );
        } catch (err) {
            console.error('Service Worker registration failed:', err);
        }
    });
}
const renderApplication = () => {
    const root = document.getElementById('root');
    if (!root) {
        throw new Error('React root element was not found.');
    }

    ReactDOM.createRoot(root).render(
        <Provider store={store}>
            <App />
        </Provider>
    );
};

if (defaultTranslations) {
    renderApplication();
} else {
    renderMissingTranslationsMessage();
}
